package at.theatervogelweide.admin.contract

import kotlinx.serialization.Serializable

@Serializable
data class SignInRequest(
    val email: String,
    val password: String,
) {
    fun validate(): List<FieldError> = validate {
        field(EMAIL, email) { required(); maxLength(255); email() }
        field(PASSWORD, password) { required() }
    }

    companion object {
        const val EMAIL = "email"
        const val PASSWORD = "password"
    }
}

/** The signed-in person, as the app shell shows it. */
@Serializable
data class Account(
    val email: String,
    val displayName: String,
    /** May manage the accounts (#66). */
    val superuser: Boolean = false,
)

/**
 * The rules for a new password (#66): long enough, no composition rules. The server also refuses passwords from its
 * list of common and breached ones. bcrypt only reads the first 72 bytes (UTF-8), so that is the maximum.
 */
object PasswordRules {
    const val MIN_LENGTH = 12
    const val MAX_BYTES = 72

    /** As the forms show it. */
    const val DESCRIPTION = "Mindestens $MIN_LENGTH Zeichen. Bekannte oder schon einmal geleakte Passwörter gehen nicht."
}

/** "Passwort ändern" for the signed-in account: `PUT /api/session/password`. */
@Serializable
data class ChangePasswordRequest(
    val currentPassword: String,
    val newPassword: String,
) {
    fun validate(): List<FieldError> = validate {
        field(CURRENT_PASSWORD, currentPassword) { required() }
        field(NEW_PASSWORD, newPassword) { newPassword() }
    }

    companion object {
        const val CURRENT_PASSWORD = "currentPassword"
        const val NEW_PASSWORD = "newPassword"
    }
}

/** The token of a one-time link, from the link's fragment, which the browser never sends on its own. */
@Serializable
data class PasswordLinkRequest(
    val token: String,
)

/** What a valid one-time link is for. */
@Serializable
data class PasswordLinkInfo(
    val email: String,
    /** A new account rather than a forgotten password. */
    val newAccount: Boolean,
)

/** Sets the password with a one-time link: `POST /api/password-link/password`. */
@Serializable
data class SetPasswordRequest(
    val token: String,
    val password: String,
) {
    fun validate(): List<FieldError> = validate {
        field(PASSWORD, password) { newPassword() }
    }

    companion object {
        const val PASSWORD = "password"
    }
}
